import { useSyncExternalStore } from 'react';
import type { Bundle } from './validate';

export type { Bundle };

export type Draft = {
  /** The published version this draft started from. */
  baseVersion: number;
  bundle: Bundle;
};

const KEY = 'argzombie.admin.contentDraft';
const listeners = new Set<() => void>();
let current: Draft | null = read();

function read(): Draft | null {
  try {
    const raw = typeof localStorage === 'undefined' ? null : localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Draft) : null;
  } catch {
    return null;
  }
}

/**
 * The content draft designers edit (kept in this browser until published). Publishing sends the whole bundle to the
 * API, which validates it with the shared rules and stores it as a new immutable version.
 */
export const draftStore = {
  get: () => current,
  set(draft: Draft | null) {
    current = draft;
    try {
      if (draft) localStorage.setItem(KEY, JSON.stringify(draft));
      else localStorage.removeItem(KEY);
    } catch {
      // Storage full or unavailable: the draft still lives in memory for this tab.
    }
    listeners.forEach((l) => l());
  },
  update(fn: (bundle: Bundle) => Bundle) {
    if (!current) return;
    draftStore.set({ ...current, bundle: fn(structuredClone(current.bundle)) });
  },
  subscribe(l: () => void) {
    listeners.add(l);
    return () => listeners.delete(l);
  },
};

export function useDraft(): Draft | null {
  return useSyncExternalStore(draftStore.subscribe, draftStore.get);
}

/**
 * The API's `409 stale_base` refusal (ADR 0059 §2.3): the draft's `baseVersion` is no longer the newest published version,
 * so publishing it would silently revert whatever was published since (for example a Story Lab save).
 */
export class StaleBaseError extends Error {
  constructor(
    readonly baseVersion: number,
    readonly currentVersion: number,
  ) {
    super(`Content changed since your draft (v${baseVersion} → v${currentVersion}).`);
    this.name = 'StaleBaseError';
  }
}
